import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon, CheckIcon, LockIcon, CreditCardIcon, SparklesIcon, ChevronLeftIcon, Loader2Icon } from 'lucide-react';
import { useBusiness } from './BusinessContext';
import { TagIcon, XCircleIcon } from 'lucide-react';
type Step = 'choose' | 'pay' | 'success';
type ReferralStatus = 'idle' | 'checking' | 'valid' | 'invalid';
// Mocked valid referral codes → discount percentage off the monthly price.
const VALID_REFERRALS: Record<string, number> = {
  TALAB10: 10,
  WELCOME: 15,
  FRIEND20: 20
};
interface UpgradePlanModalProps {
  onClose: () => void;
  initialPlan?: string;
}
export function UpgradePlanModal({
  onClose,
  initialPlan
}: UpgradePlanModalProps) {
  const {
    trialPlan,
    subscribePackage,
    PACKAGES,
    currentPlanId
  } = useBusiness();
  const [step, setStep] = useState<Step>('choose');
  const [selected, setSelected] = useState<string>(initialPlan ?? trialPlan ?? 'Growth');
  const [processing, setProcessing] = useState(false);
  const [referral, setReferral] = useState('');
  const [referralStatus, setReferralStatus] = useState<ReferralStatus>('idle');
  const [discountPct, setDiscountPct] = useState(0);
  const paidPackages = PACKAGES.filter(p => p.id !== 'Free');
  const plan = PACKAGES.find(p => p.id === selected) || paidPackages[0];
  const discount = Math.round(plan.price * discountPct / 100);
  const totalDue = plan.price - discount;
  const applyReferral = () => {
    const code = referral.trim().toUpperCase();
    if (!code) return;
    setReferralStatus('checking');
    setTimeout(() => {
      const pct = VALID_REFERRALS[code];
      if (pct) {
        setDiscountPct(pct);
        setReferralStatus('valid');
      } else {
        setDiscountPct(0);
        setReferralStatus('invalid');
      }
    }, 800);
  };
  const clearReferral = () => {
    setReferral('');
    setReferralStatus('idle');
    setDiscountPct(0);
  };
  const pay = () => {
    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      subscribePackage(selected);
      setStep('success');
    }, 1600);
  };
  const handleBack = () => {
    if (step === 'pay') {
      setStep('choose');
    } else {
      onClose();
    }
  };
  const mountNode = typeof document !== 'undefined' && document.getElementById('app-screen-root') || (typeof document !== 'undefined' ? document.body : null);
  if (!mountNode) return null;
  return createPortal(<AnimatePresence data-id="element-2059">
      <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      damping: 28,
      stiffness: 250
    }} className="absolute inset-0 z-[100] bg-[#F8FAFC] flex flex-col overflow-hidden" data-id="element-2060">
        {/* Sticky Mobile Header */}
        <div className="flex-shrink-0 bg-white border-b border-slate-100 z-20" data-id="element-2061">
          <div className="flex items-center gap-2 px-3 py-3" data-id="element-2062">
            <button onClick={handleBack} aria-label="Back" className="p-2 -ml-1 text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-2063">
              <ChevronLeftIcon className="w-6 h-6" data-id="element-2064" />
            </button>
            <div className="flex-1 min-w-0" data-id="element-2065">
              <h2 className="text-lg font-bold text-dark-navy truncate" data-id="element-2066">
                {step === 'choose' && 'Choose your plan'}
                {step === 'pay' && 'Payment details'}
                {step === 'success' && 'Subscription active'}
              </h2>
              <p className="text-xs text-slate-500 truncate" data-id="element-2067">
                {step === 'choose' && 'Upgrade your Business Hub anytime.'}
                {step === 'pay' && `Upgrading to the ${selected} plan.`}
                {step === 'success' && 'Thank you for upgrading.'}
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4" data-id="element-2068">
          {step === 'choose' && <div className="flex flex-col gap-3.5 pb-4" data-id="element-2069">
              {paidPackages.map(p => {
            const isSel = selected === p.id;
            const isCurrent = currentPlanId === p.id;
            return <button key={p.id} onClick={() => setSelected(p.id)} className={`text-left rounded-2xl p-4 border-2 transition-all relative ${isSel ? 'border-primary-indigo bg-indigo-50/60 shadow-sm' : 'border-slate-200 bg-white'}`} data-id="element-2070">
                    <div className="flex items-start justify-between gap-3 mb-2" data-id="element-2071">
                      <div className="min-w-0" data-id="element-2072">
                        <div className="flex items-center gap-2 mb-0.5" data-id="element-2073">
                          <span className="font-bold text-dark-navy" data-id="element-2074">
                            {p.name}
                          </span>
                          {p.popular && <span className="px-2 py-0.5 bg-ai-purple text-white text-[9px] font-bold rounded-full uppercase tracking-wide" data-id="element-2075">
                              Popular
                            </span>}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-2076">
                          {p.listingLimit === Infinity ? '100+ listings' : `${p.listingLimit} listings`}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0" data-id="element-2077">
                        <div className="text-right" data-id="element-2078">
                          <div className="text-primary-indigo font-display font-bold text-xl leading-none" data-id="element-2079">
                            AED {p.price.toLocaleString()}
                          </div>
                          <div className="text-[11px] text-slate-400 font-normal" data-id="element-2080">
                            per month
                          </div>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isSel ? 'border-primary-indigo bg-primary-indigo' : 'border-slate-300'}`} data-id="element-2081">
                          {isSel && <CheckIcon className="w-3 h-3 text-white" data-id="element-2082" />}
                        </div>
                      </div>
                    </div>
                    <div className="space-y-1.5 mt-3 pt-3 border-t border-slate-100" data-id="element-2083">
                      {p.features.map(f => <div key={f} className="flex items-start gap-1.5 text-xs text-slate-600" data-id="element-2084">
                          <CheckIcon className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2085" />
                          {f}
                        </div>)}
                    </div>
                    {isCurrent && <div className="mt-3 text-center text-[11px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-2086">
                        Current Plan
                      </div>}
                  </button>;
          })}
            </div>}

          {step === 'pay' && <div className="flex flex-col gap-4 pb-4" data-id="element-2087">
              {/* Order summary first on mobile */}
              <div className="bg-white border border-slate-200 rounded-2xl p-4" data-id="element-2088">
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-2089">
                  Order Summary
                </div>
                <div className="flex items-center justify-between mb-1.5" data-id="element-2090">
                  <span className="text-sm text-slate-600" data-id="element-2091">
                    {plan.name} plan
                  </span>
                  <span className="font-bold text-dark-navy" data-id="element-2092">
                    AED {plan.price.toLocaleString()}
                  </span>
                </div>
                <div className="text-sm text-slate-500 mb-3" data-id="element-2093">
                  Billed monthly
                </div>
                {referralStatus === 'valid' && <div className="flex items-center justify-between mb-1.5" data-id="element-2094">
                    <span className="text-sm text-emerald-600 flex items-center gap-1.5" data-id="element-2095">
                      <TagIcon className="w-3.5 h-3.5" data-id="element-2096" />
                      Referral ({discountPct}% off)
                    </span>
                    <span className="font-bold text-emerald-600" data-id="element-2097">
                      − AED {discount.toLocaleString()}
                    </span>
                  </div>}
                <div className="h-px bg-slate-100 my-3" data-id="element-2098" />
                <div className="flex items-center justify-between" data-id="element-2099">
                  <span className="font-bold text-dark-navy" data-id="element-2100">Total today</span>
                  <span className="font-display font-bold text-xl text-primary-indigo" data-id="element-2101">
                    AED {totalDue.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Referral code */}
              <div className="bg-white border border-slate-200 rounded-2xl p-4" data-id="element-2102">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2103">
                  Referral code
                </label>
                <div className="flex gap-2" data-id="element-2104">
                  <div className="relative flex-1" data-id="element-2105">
                    <TagIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-2106" />
                    <input value={referral} onChange={e => {
                  setReferral(e.target.value);
                  if (referralStatus !== 'idle') setReferralStatus('idle');
                }} placeholder="Enter code" disabled={referralStatus === 'valid'} className={`w-full pl-9 pr-3 py-2.5 bg-slate-50 border rounded-xl text-sm uppercase outline-none focus:ring-2 focus:ring-primary-indigo/20 disabled:opacity-70 ${referralStatus === 'invalid' ? 'border-rose-300' : referralStatus === 'valid' ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200'}`} data-id="element-2107" />
                  </div>
                  {referralStatus === 'valid' ? <button onClick={clearReferral} className="px-4 py-2.5 bg-slate-100 text-slate-600 rounded-xl text-sm font-bold active:scale-95 transition-transform flex-shrink-0" data-id="element-2108">
                      Remove
                    </button> : <button onClick={applyReferral} disabled={!referral.trim() || referralStatus === 'checking'} className="px-4 py-2.5 bg-dark-navy text-white rounded-xl text-sm font-bold active:scale-95 transition-transform flex-shrink-0 disabled:opacity-50 flex items-center gap-1.5" data-id="element-2109">
                      {referralStatus === 'checking' ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-2110" /> : 'Apply'}
                    </button>}
                </div>
                {referralStatus === 'valid' && <p className="mt-2 flex items-center gap-1.5 text-[12px] font-medium text-emerald-600" data-id="element-2111">
                    <CheckCircle2Icon className="w-3.5 h-3.5" data-id="element-2112" />
                    Code applied — {discountPct}% off your subscription.
                  </p>}
                {referralStatus === 'invalid' && <p className="mt-2 flex items-center gap-1.5 text-[12px] font-medium text-rose-600" data-id="element-2113">
                    <XCircleIcon className="w-3.5 h-3.5" data-id="element-2114" />
                    That code isn&apos;t valid. Please check and try again.
                  </p>}
              </div>

              {/* Card form */}
              <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-4" data-id="element-2115">
                <Field label="Cardholder name" placeholder="Fadi Al Mansoori" data-id="element-2116" />
                <div data-id="element-2117">
                  <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2118">
                    Card number
                  </label>
                  <div className="relative" data-id="element-2119">
                    <input placeholder="1234 5678 9012 3456" className="w-full pl-4 pr-11 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-indigo/20" data-id="element-2120" />
                    <CreditCardIcon className="w-5 h-5 text-slate-300 absolute right-3 top-1/2 -translate-y-1/2" data-id="element-2121" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3" data-id="element-2122">
                  <Field label="Expiry" placeholder="MM / YY" data-id="element-2123" />
                  <Field label="CVC" placeholder="123" data-id="element-2124" />
                </div>
                <Field label="Billing country" placeholder="United Arab Emirates" data-id="element-2125" />
              </div>
            </div>}

          {step === 'success' && <div className="text-center py-10 px-2" data-id="element-2126">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2127">
                <CheckCircle2Icon className="w-11 h-11 text-green-600" data-id="element-2128" />
              </motion.div>
              <h3 className="text-xl font-display font-bold text-dark-navy mb-2" data-id="element-2129">
                Payment successful
              </h3>
              <p className="text-slate-600 text-sm mb-6" data-id="element-2130">
                Your{' '}
                <span className="font-bold text-dark-navy" data-id="element-2131">{selected}</span>{' '}
                plan is now active. Enjoy the full Business Hub.
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 rounded-xl" data-id="element-2132">
                <SparklesIcon className="w-4 h-4 text-primary-indigo" data-id="element-2133" />
                <span className="text-sm font-medium text-primary-indigo" data-id="element-2134">
                  {selected} Plan · Active
                </span>
              </div>
            </div>}
        </div>

        {/* Sticky Footer CTA */}
        {step === 'choose' && <div className="flex-shrink-0 bg-white border-t border-slate-100 px-4 py-3" data-id="element-2135">
            <button onClick={() => setStep('pay')} className="w-full py-3.5 bg-primary-indigo text-white font-bold rounded-xl active:scale-[0.98] transition-transform shadow-sm" data-id="element-2136">
              Continue to payment
            </button>
          </div>}
        {step === 'pay' && <div className="flex-shrink-0 bg-white border-t border-slate-100 px-4 py-3" data-id="element-2137">
            <button onClick={pay} disabled={processing} className="w-full py-3.5 bg-primary-indigo text-white font-bold rounded-xl active:scale-[0.98] transition-transform flex items-center justify-center gap-2 disabled:opacity-70 shadow-sm" data-id="element-2138">
              {processing ? <>
                  <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-2139" /> Processing…
                </> : <>
                  <LockIcon className="w-4 h-4" data-id="element-2140" /> Pay AED{' '}
                  {totalDue.toLocaleString()}
                </>}
            </button>
            <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400 mt-2.5" data-id="element-2141">
              <LockIcon className="w-3 h-3" data-id="element-2142" /> Secured & encrypted
            </p>
          </div>}
        {step === 'success' && <div className="flex-shrink-0 bg-white border-t border-slate-100 px-4 py-3" data-id="element-2143">
            <button onClick={onClose} className="w-full py-3.5 bg-primary-indigo text-white font-bold rounded-xl active:scale-[0.98] transition-transform shadow-sm" data-id="element-2144">
              Done
            </button>
          </div>}
      </motion.div>
    </AnimatePresence>, mountNode);
}
function Field({
  label,
  placeholder
}: {
  label: string;
  placeholder: string;
}) {
  return <div data-id="element-2145">
      <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2146">
        {label}
      </label>
      <input placeholder={placeholder} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-indigo/20" data-id="element-2147" />
    </div>;
}